import { odooApi } from '../index';
import { Facility, PaginatedResponse } from '@/types';
import { getOdooLang, buildImageUrl } from '@/config/api.config';
import { 
  transformOdooPaginatedResponse,
  extractErrorMessage
} from '../transforms';

/**
 * Facilities API Endpoints (Connected to Odoo Backend)
 * 
 * This file contains all facility-related API endpoints.
 * Connected to real Odoo API at /api/moys/facilities
 */

interface OdooFacility {
  id?: number;
  name: string; // Translated based on lang parameter
  facility_type?: 'stadium' | 'club' | 'academy' | 'youth_forum' | 'hall' | 'center' | 'pool' | 'gym' | 'other';
  facility_type_display?: string;
  city?: string; // Translated
  district?: string; // Translated
  address?: string; // Translated
  full_address?: string; // Full address as single string
  latitude: number;
  longitude: number;
  map_location?: string;
  image?: string | null;
  image_1920?: string | null; // Alternative image field name
  images?: Array<{
    id: number;
    name: string;
    file_url: string;
    mimetype: string;
    file_size: number;
    create_date: string;
    description: string;
  }>;
  tag_ids?: Array<{
    id: number;
    name: string;
    color: number;
  }>;
  tags?: Array<{
    id: number;
    name: string;
    color: number;
  }>;
  description?: string; // HTML, translated
  summary?: string; // Translated
  capacity?: number;
  area?: number;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  working_hours?: string | null;
  facebook?: string | null;
  instagram?: string | null;
  whatsapp?: string | null;
  status?: 'operational' | 'under_construction' | 'maintenance' | 'closed';
  status_display?: string;
  opening_date?: string | null;
  visitor_count?: number;
  manager?: string | null;
  department?: string | null;
  facility_category?: string;
  affiliated_department?: string;
  affiliated_to?: string;
  main_activities?: string;
  constructions?: string;
  participants_count?: number;
  founding_year?: string;
  opening_year?: string;
  facility_manager?: string;
  current_occupant?: string;
  plot_number?: string;
  land_ownership?: string;
  property_type?: string;
  total_area?: string;
  project_status?: string;
  current_condition?: string;
  notes?: string;
}

// Backend sends '' for empty text fields
const text = (v?: string | null) => (typeof v === 'string' && v.trim() ? v.trim() : undefined);

function transformOdooFacilityToFacility(
  odooFacility: OdooFacility,
  lang: 'ar' | 'en' | 'ku',
  fallbackIndex?: number
): Facility {
  // Map backend facility_type (name string) to frontend FacilityType
  // Backend returns facility_type_id.name, so we need to handle both codes and display names
  const facilityTypeMap: Record<string, Facility['type']> = {
    // Code values
    'stadium': 'stadium',
    'club': 'club',
    'academy': 'academy',
    'youth_forum': 'forum',
    'hall': 'hall', // Map hall to hall (separate type)
    'center': 'other', // Map center to other
    'pool': 'pool',
    'gym': 'gym',
    'other': 'other',
    // Display name values (case-insensitive matching)
    'ملعب': 'stadium',
    'نادي': 'club',
    'أكاديمية': 'academy',
    'منتدى شبابي': 'forum',
    'قاعة': 'hall',
    'مركز': 'other',
    'مسبح': 'pool',
    'صالة رياضية': 'gym',
    'forums and departments': 'forum',
    'منتديات والمديريات': 'forum',
  };

  // Backend types are free-form records, so exact keys first, then keywords.
  // The mapped type only picks the marker icon; the label comes from the backend.
  const rawType = (odooFacility.facility_type_display || odooFacility.facility_type || '').trim();
  const normalizedType = rawType.toLowerCase();
  const keywordTypes: Array<[RegExp, Facility['type']]> = [
    [/ملعب|ملاعب|stadium|یاریگا/i, 'stadium'],
    [/مسبح|مسابح|pool/i, 'pool'],
    [/موهبة|أكاديمية|اكاديمية|academy|talent/i, 'academy'],
    [/قاعة|قاعات|هۆڵ|hall/i, 'hall'],
    [/منتدى|منتديات|مديرية|مديريات|مركز شباب|forum|department|youth/i, 'forum'],
    [/نادي|اندية|club/i, 'club'],
    [/صالة|gym/i, 'gym'],
  ];
  const frontendType =
    facilityTypeMap[normalizedType] ||
    facilityTypeMap[(odooFacility.facility_type || '').toLowerCase().trim()] ||
    keywordTypes.find(([re]) => re.test(rawType))?.[1] ||
    'other';

  // Extract photos from images array or use image/image_1920
  const photos: string[] = [];
  if (odooFacility.images && Array.isArray(odooFacility.images)) {
    odooFacility.images.forEach((img) => {
      const url = buildImageUrl(img.file_url) || img.file_url;
      if (url) photos.push(url);
    });
  }
  // Add main image if available (check both image and image_1920)
  const mainImage = odooFacility.image_1920 || odooFacility.image;
  if (mainImage) {
    const imageUrl = buildImageUrl(mainImage) || mainImage;
    if (imageUrl && !photos.includes(imageUrl)) {
      photos.unshift(imageUrl);
    }
  }

  // Extract amenities from tags (check both tag_ids and tags)
  const tags = odooFacility.tag_ids || odooFacility.tags || [];
  const amenities = tags.map((tag) => tag.name).filter(Boolean);

  // Parse address to extract city and province
  // Try to extract from full_address or use city/district
  let city = odooFacility.city || '';
  let province = odooFacility.district || odooFacility.city || '';
  
  if (odooFacility.full_address) {
    // Try to parse full_address (format: "address, city, province, country")
    const addressParts = odooFacility.full_address.split(',').map(s => s.trim());
    if (addressParts.length >= 2) {
      // Usually city is second to last, province might be before that
      city = addressParts[addressParts.length - 2] || city;
      province = addressParts[addressParts.length - 3] || addressParts[addressParts.length - 2] || province;
    }
  }

  // Use facility ID or generate one from index
  const facilityId = odooFacility.id?.toString() || `facility-${fallbackIndex ?? Date.now()}`;

  return {
    id: facilityId,
    name: {
      ar: odooFacility.name,
      en: odooFacility.name,
      ku: odooFacility.name,
    },
    type: frontendType,
    typeLabel: rawType || undefined,
    province: province || city || '',
    city: city || '',
    lat: odooFacility.latitude || 0,
    lng: odooFacility.longitude || 0,
    photos: photos.length > 0 ? photos : [],
    description: {
      ar: odooFacility.description || odooFacility.summary || '',
      en: odooFacility.description || odooFacility.summary || '',
      ku: odooFacility.description || odooFacility.summary || '',
    },
    capacity: odooFacility.capacity || undefined,
    amenities: amenities.length > 0 ? amenities : undefined,
    contact:
      odooFacility.phone ||
      odooFacility.email ||
      odooFacility.website ||
      odooFacility.facebook ||
      odooFacility.instagram ||
      odooFacility.whatsapp
        ? {
            phone: odooFacility.phone || undefined,
            email: odooFacility.email || undefined,
            website: odooFacility.website || undefined,
            facebook: odooFacility.facebook || undefined,
            instagram: odooFacility.instagram || undefined,
            whatsapp: odooFacility.whatsapp || undefined,
          }
        : undefined,
    address: odooFacility.full_address || odooFacility.address || undefined,
    workingHours: odooFacility.working_hours || undefined,
    details: {
      category: text(odooFacility.facility_category),
      affiliatedDepartment: text(odooFacility.affiliated_department),
      affiliatedTo: text(odooFacility.affiliated_to),
      mainActivities: text(odooFacility.main_activities),
      constructions: text(odooFacility.constructions),
      participantsCount: odooFacility.participants_count || undefined,
      foundingYear: text(odooFacility.founding_year),
      openingYear: text(odooFacility.opening_year),
      manager: text(odooFacility.facility_manager) || text(odooFacility.manager),
      currentOccupant: text(odooFacility.current_occupant),
      plotNumber: text(odooFacility.plot_number),
      landOwnership: text(odooFacility.land_ownership),
      propertyType: text(odooFacility.property_type),
      totalArea: text(odooFacility.total_area),
      projectStatus: text(odooFacility.project_status),
      currentCondition: text(odooFacility.current_condition),
      notes: text(odooFacility.notes),
    },
    createdAt: odooFacility.opening_date || new Date().toISOString(),
  };
}

export const facilitiesApi = odooApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all facilities from Odoo with pagination and filters
    getFacilities: builder.query<Facility[], { 
      lang?: 'ar' | 'en' | 'ku';
      facility_type?: string;
      city?: string;
      status?: string;
      page?: number;
      pageSize?: number;
    }>({
      query: ({ lang = 'ar', facility_type, city, status, page = 1, pageSize = 1000 }) => {
        const odooLang = getOdooLang(lang);
        
        // Backend expects page and page_size via _get_pagination_params
        const params = new URLSearchParams({
          lang: odooLang,
          page: String(page),
          page_size: String(pageSize),
        });
        
        // Note: Backend doesn't currently support these filters, but we keep them for future use
        if (facility_type) {
          params.append('facility_type', facility_type);
        }
        if (city) {
          params.append('city', city);
        }
        if (status) {
          params.append('status', status);
        }
        
        return {
          url: `/api/moys/facilities?${params.toString()}`,
          method: 'POST',
        };
      },
      transformResponse: (response: any, meta, arg) => {
        try {
          if (!response.success || !response.data) {
            console.warn('[Facilities API] Invalid response format:', response);
            return [];
          }
          
          // Transform each facility with index as fallback for id
          const facilities = response.data.map((item: OdooFacility, index: number) => {
            // Ensure id exists (use index if missing)
            const facilityWithId = {
              ...item,
              id: item.id || index + 1,
            };
            return transformOdooFacilityToFacility(facilityWithId, arg.lang || 'ar', index);
          });
          
          return facilities;
        } catch (error) {
          console.error('[Facilities API] Transform error:', error);
          throw error;
        }
      },
      transformErrorResponse: (response) => {
        return extractErrorMessage(response);
      },
      providesTags: (result) =>
        result && Array.isArray(result)
          ? [
              ...result.map(({ id }) => ({ type: 'Facility' as const, id })),
              { type: 'Facility', id: 'LIST' },
            ]
          : [{ type: 'Facility', id: 'LIST' }],
    }),

    // Get single facility by ID
    getFacility: builder.query<Facility, { id: string; lang?: 'ar' | 'en' | 'ku' }>({
      async queryFn({ id, lang = 'ar' }, api, extraOptions, baseQuery) {
        // Fetch all facilities (up to 1000) and find the one we need
        // TODO: Backend should implement GET /api/moys/facilities/{id} endpoint
        const odooLang = getOdooLang(lang);
        const result = await baseQuery({
          url: `/api/moys/facilities?lang=${odooLang}&limit=1000&offset=0`,
          method: 'POST',
        });
        
        if (result.error) {
          return { error: result.error as any };
        }
        
        try {
          const response = result.data as any;
          
          if (!response.success || !response.data) {
            return { error: { status: 500, data: 'Invalid response format' } as any };
          }
          
          const facilities = response.data.map((item: OdooFacility, index: number) => {
            const facilityWithId = {
              ...item,
              id: item.id || index + 1,
            };
            return transformOdooFacilityToFacility(facilityWithId, lang, index);
          });
          
          const facility = facilities.find((f: Facility) => f.id === id);
          
          if (facility) {
            return { data: facility };
          }
          
          return { error: { status: 404, data: 'Facility not found' } as any };
        } catch (error) {
          console.error('[Facilities API] Get single facility error:', error);
          return { error: { status: 500, data: extractErrorMessage(error) } as any };
        }
      },
      providesTags: (result, error, { id }) => [{ type: 'Facility', id }],
    }),
  }),
});

export const {
  useGetFacilitiesQuery,
  useGetFacilityQuery,
} = facilitiesApi;

